import { DataGrid } from '@mui/x-data-grid'
import { Box, IconButton, Button, Tooltip } from '@mui/material'
import { useNavigate } from 'react-router-dom'
import TextField from '@mui/material/TextField'
import { useSearchParams } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import { getAllTags } from '../../graphql/queries'
import { useState, useEffect, useRef } from 'react'
import CloseIcon from '@mui/icons-material/Close'
import ContentCopyIcon from '@mui/icons-material/ContentCopy'
import LocalOfferIcon from '@mui/icons-material/LocalOffer'

const columns = [
  {
    field: 'tableIndex',
    headerName: '#',
    filterable: false,
    minWidth: 120,
    maxWidth: 170,
    flex: 1,
    sortable: false,
    renderCell: params => (
      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center', alignItems: 'center' }}>
        {params.value}
        <DetailPage id={params.row.id} />
      </Box>
    ),
  },
  {
    field: 'name',
    headerName: 'Tag Name',
    sortable: false,
    minWidth: 200,
    flex: 1,
    renderCell: params => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        <LocalOfferIcon sx={{ fontSize: 16, color: '#1976d2' }} />
        {params.value}
      </Box>
    ),
  },
  {
    field: 'createdAt',
    headerName: 'Created At',
    sortable: false,
    minWidth: 120,
    flex: 1,
    valueGetter: params => new Date(params.row.createdAt).toLocaleDateString('tr-TR'),
  },
  {
    field: 'updatedAt',
    headerName: 'Updated At',
    sortable: false,
    minWidth: 120,
    flex: 1,
    valueGetter: params => new Date(params.row.updatedAt).toLocaleDateString('tr-TR'),
  },
  {
    field: 'id',
    headerName: 'Tag ID',
    sortable: false,
    minWidth: 220,
    flex: 1,
    renderCell: params => (
      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
        {params.value}
        <CopyCell value={params.value} />
      </Box>
    ),
  },
]

export function DetailPage({ id }) {
  const navigate = useNavigate()

  const handleNavigate = async () => {
    navigate(`/tags/${id}`)
  }
  return (
    <Tooltip title="View Tag Details">
      <Button
        onClick={handleNavigate}
        variant="outlined"
        className="actionBtn"
        sx={{ fontSize: {xs:'0.7rem', md:'small'} }}
      >
        Details
      </Button>
    </Tooltip>
  )
}

export function CopyCell({ value }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    await navigator.clipboard.writeText(value)
    setCopied(true)
    setTimeout(() => setCopied(false), 1500)
  }

  return (
    <Tooltip title={copied ? 'Copied!' : 'Copy'}>
      <IconButton size="small" onClick={handleCopy}>
        <ContentCopyIcon fontSize="inherit" />
      </IconButton>
    </Tooltip>
  )
}

export default function TagTable() {
  const [searchParams, setSearchParams] = useSearchParams()
  const initialSearch = searchParams.get('search') || ''
  const [search, setSearch] = useState(initialSearch)
  const searchRef = useRef(null)

  const { loading, error, data } = useQuery(getAllTags)

  useEffect(() => {
    setSearchParams({
      search: search,
    })
  }, [search, setSearchParams])

  const handleSearchChange = (event) => {
    setSearch(event.target.value)
  }

  const handleClearSearch = () => {
    setSearch('')
    if (searchRef.current) {
      searchRef.current.focus()
    }
  }

  if (loading) return <div>Loading...</div>
  if (error) return <div>Error: {error.message}</div>

  const tags = data?.getAllTags || []
  
  // Filter tags based on search
  const filteredTags = tags.filter(tag =>
    tag.name.toLowerCase().includes(search.toLowerCase()) ||
    tag.id.toLowerCase().includes(search.toLowerCase())
  )

  const rowsData = filteredTags.map((tag, index) => ({
    ...tag,
    tableIndex: index + 1,
  }))

  return (
    <Box sx={{ height: 600, width: '100%' }}>
      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <TextField
          ref={searchRef}
          label="Search tags..."
          variant="outlined"
          value={search}
          onChange={handleSearchChange}
          size="small"
          sx={{ minWidth: 300 }}
        />
        {search && (
          <IconButton onClick={handleClearSearch} size="small">
            <CloseIcon />
          </IconButton>
        )}
      </Box>
      
      <DataGrid
        rows={rowsData}
        columns={columns}
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: { page: 0, pageSize: 25 },
          },
        }}
        disableRowSelectionOnClick
        disableColumnMenu
        disableColumnSorting
      />
    </Box>
  )
} 